import { useAppSelector, useAppDispatch } from 'store';
import { CHARTS_TITLES } from 'utils/constants';
import { chartsFormConfig } from 'utils/constants/chartsForm';
import { currentArrField, avgDealSizeField } from 'utils/constants/fields';
import { CalculateFieldNames } from 'utils/constants/fieldNames';
import { setChartField } from 'store/chartForms/chartForms.slice';

import { useEffect, useState } from 'react';
import ChartsFormNestedFields from './ChartsFormNestedFields';
import ChartsFormTextField from './ChartsFormTextField';
import { ChartFormField } from 'interfaces/components/modalTextField';
import { ChartFieldValue } from 'interfaces/store/chartForms';
import { populateChartFormsFromCompanyInfo } from 'utils/helpers/populateChartForms';
import ApplyButton from './ApplyButton';

const ChartsForms = () => {
  const dispatch = useAppDispatch();
  const selectedChartIndex = useAppSelector(
    state => state.chartsInfo.selectedChartIndex
  );
  const selectedChartTitle = CHARTS_TITLES[selectedChartIndex];
  const chartConfig = chartsFormConfig[selectedChartTitle];
  const [showNestedFields, setShowNestedFields] = useState(false);

  const companyInfo = useAppSelector(state => state.companyInfo);

  const xAxisSwitch = useAppSelector(state => state.chartSwitches.xAxisSwitch);
  const fieldsValues = useAppSelector(state => state.chartForms.fields);

  const annualRevenue = fieldsValues[CalculateFieldNames.ANNUAL_REVENUE];

  // Populate chart forms from companyInfo when component mounts
  useEffect(() => {
    populateChartFormsFromCompanyInfo(companyInfo, dispatch);
  }, [companyInfo, dispatch]);

  useEffect(() => {
    if (chartConfig.nestedFields && showNestedFields) {
      chartConfig.calculatedFields.forEach(({ field, calculate }) => {
        if (typeof calculate === 'function') {
          const result = calculate(fieldsValues);
          dispatch(
            setChartField({
              field: field.name,
              value: result ?? ''
            })
          );
        }
      });
    }
  }, [
    chartConfig.nestedFields,
    chartConfig.calculatedFields,
    fieldsValues,
    dispatch,
    showNestedFields
  ]);

  const handleFieldChange = (name: ChartFieldValue, value: string) => {
    dispatch(setChartField({ field: name, value }));
  };

  const getFieldValue = (name: ChartFieldValue) => {
    const value = fieldsValues[name];
    return value === null || value === 'NaN' ? '' : value;
  };

  const handleCalculateButtonClick = () => {
    setShowNestedFields(!showNestedFields);
  };

  if (!chartConfig) return null;

  return (
    <div className='flex flex-col items-start justify-center gap-4 w-full'>
      <ChartsFormTextField
        field={xAxisSwitch ? currentArrField : avgDealSizeField}
        disabled={false}
        getFieldValue={getFieldValue}
        handleFieldChange={handleFieldChange}
      />
      {chartConfig.title && <p className='text-sm'>{chartConfig.title}</p>}
      {chartConfig.calculatedFields.map(({ field }) => (
        <ChartsFormTextField
          key={`chart-form-text-field-${field.name}`}
          field={field as ChartFormField}
          disabled={showNestedFields}
          getFieldValue={getFieldValue}
          handleFieldChange={handleFieldChange}
        />
      ))}
      <div
        className={`flex w-full ${
          showNestedFields
            ? 'flex-col items-end gap-4'
            : `${
                chartConfig.nestedFields ? 'justify-between gap-2' : ''
              } items-center`
        }`}
      >
        <ChartsFormNestedFields
          showNestedFields={showNestedFields}
          nestedFields={chartConfig.nestedFields}
          getFieldValue={getFieldValue}
          handleFieldChange={handleFieldChange}
          handleCalculateButtonClick={handleCalculateButtonClick}
          annualRevenue={annualRevenue}
        />
        <ApplyButton
          showNestedFields={showNestedFields}
          xAxisSwitch={xAxisSwitch}
          fieldsValues={fieldsValues}
          chartConfig={chartConfig}
        />
      </div>
    </div>
  );
};

export default ChartsForms;
